/**
 * Build CSV for Vehicle Cost Rates — Capital, Standing (+ trailer pool), Running.
 * Wide layout matches the on-screen tables (field rows × class columns).
 */

import type {
  FleetCostVehicleType,
  TrailerStandingAssumptions,
} from '../types/fleetCostAssumptions';
import type {
  ProfitabilityAssumptionsConfig,
  ProfitabilityDriverCostRate,
} from '../types/profitabilityTruth';
import { PROFITABILITY_TONNAGE_CLASSES } from '../types/profitabilityTruth';
import {
  ACQUISITION_METHOD_LABELS,
  acquisitionMethodOf,
  acquisitionMethodsInUse,
  acquisitionMixCounts,
  capitalAnnualisedGbp,
  RENTAL_PERIOD_LABELS,
  rentalPeriodOf,
  vehicleCapitalAnnualisedGbp,
} from './fleetCapitalAnnualised';
import { trailerTypeAnnualPerTrailerGbp } from './fleetCostTrailerStanding';
import { fleetStandingAnnualGbp, FLEET_STANDING_OP_DEFAULTS } from './fleetCostStandingPerDay';

function csvEscape(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(cells: Array<string | number | boolean | null | undefined>): string {
  return cells.map(csvEscape).join(',');
}

function displayClass(name: string): string {
  return name === 'Artic' ? '44T' : name;
}

function numOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export interface FleetCostAssumptionsCsvInput {
  vehicleTypes: FleetCostVehicleType[];
  quantityByName: Record<string, number>;
  trailerStanding: TrailerStandingAssumptions;
  profit: ProfitabilityAssumptionsConfig | null | undefined;
  /** Emergency appliance tables omit trailer pool section. */
  includeTrailerPool?: boolean;
  runningClassKeys?: readonly string[];
}

/** Multi-section CSV covering Capital, Standing, Trailer pool, and Running. */
export function buildFleetCostAssumptionsCsv(input: FleetCostAssumptionsCsvInput): string {
  const {
    vehicleTypes,
    quantityByName,
    trailerStanding,
    profit,
    includeTrailerPool = true,
    runningClassKeys = PROFITABILITY_TONNAGE_CLASSES as readonly string[],
  } = input;

  const classHeaders = vehicleTypes.map((v) => displayClass(v.name));
  const lines: string[] = [];

  const pushWide = (
    section: string,
    field: string,
    values: Array<string | number>,
  ) => {
    lines.push(csvRow([section, field, ...values]));
  };

  lines.push(csvRow(['Section', 'Field', ...classHeaders]));

  // ── Capital ──────────────────────────────────────────────────────────────
  pushWide(
    'Capital Costs',
    'Quantity',
    vehicleTypes.map((v) => quantityByName[v.name] ?? 0),
  );
  const mixCount = (method: 'purchase' | 'lease' | 'rental') =>
    vehicleTypes.map((v) => {
      const mix = acquisitionMixCounts(v);
      if (!mix.entered) return '';
      return mix.counts[method] - (method === 'purchase' ? mix.unset : 0);
    });
  pushWide('Capital Costs', 'Purchased Vehicles', mixCount('purchase'));
  pushWide('Capital Costs', 'Leased Vehicles', mixCount('lease'));
  pushWide('Capital Costs', 'Rented Vehicles', mixCount('rental'));
  pushWide(
    'Capital Costs',
    'Acquisition Not Set (counted as purchase)',
    vehicleTypes.map((v) => {
      const mix = acquisitionMixCounts(v);
      return mix.entered ? mix.unset : '';
    }),
  );
  pushWide(
    'Capital Costs',
    'Purchase Price (£)',
    vehicleTypes.map((v) => numOr(v.unitCost, 0)),
  );
  pushWide(
    'Capital Costs',
    'Initial Taxes/Fees (£)',
    vehicleTypes.map((v) => numOr(v.initialTaxesFees, 2000)),
  );
  pushWide(
    'Capital Costs',
    'Useful Life (yr)',
    vehicleTypes.map((v) => numOr(v.serviceLife, 0)),
  );
  pushWide(
    'Capital Costs',
    'Residual Value (%)',
    vehicleTypes.map((v) => numOr(v.residualValue, 15)),
  );
  // Lease / rental rows only when a class uses them; blank for other classes.
  const methodValue = (
    method: 'lease' | 'rental',
    get: (v: FleetCostVehicleType) => string | number,
  ) => vehicleTypes.map((v) => (acquisitionMethodsInUse(v).includes(method) ? get(v) : ''));
  if (vehicleTypes.some((v) => acquisitionMethodsInUse(v).includes('lease'))) {
    pushWide('Capital Costs', 'Lease (£/month)', methodValue('lease', (v) => numOr(v.leaseMonthly, 0)));
    pushWide('Capital Costs', 'Lease Term (months)', methodValue('lease', (v) => numOr(v.leaseTermMonths, 12)));
    pushWide('Capital Costs', 'Initial Rental / Deposit (£)', methodValue('lease', (v) => numOr(v.leaseUpfront, 0)));
    pushWide('Capital Costs', 'End-of-Term Fees (£)', methodValue('lease', (v) => numOr(v.leaseEndFees, 0)));
    pushWide(
      'Capital Costs',
      'Maintenance Included',
      methodValue('lease', (v) => (v.leaseMaintenanceIncluded ? 'Yes' : 'No')),
    );
  }
  if (vehicleTypes.some((v) => acquisitionMethodsInUse(v).includes('rental'))) {
    pushWide('Capital Costs', 'Rental Rate (£)', methodValue('rental', (v) => numOr(v.rentalRate, 0)));
    pushWide('Capital Costs', 'Rental Period', methodValue('rental', (v) => RENTAL_PERIOD_LABELS[rentalPeriodOf(v)]));
  }
  pushWide(
    'Capital Costs',
    'Total (£/yr/veh)',
    vehicleTypes.map((v) => vehicleCapitalAnnualisedGbp(v)),
  );

  // ── Standing ─────────────────────────────────────────────────────────────
  const standingFields: Array<{ label: string; field: keyof typeof FLEET_STANDING_OP_DEFAULTS }> = [
    { label: 'Insurance (£/yr)', field: 'insurance' },
    { label: 'Road Tax (£/yr)', field: 'roadTax' },
    { label: 'O-License (£/yr)', field: 'oLicense' },
    { label: 'Telematics (£/mo)', field: 'telematics' },
    { label: 'Cleaning (£/mo)', field: 'cleaning' },
    { label: 'Parking (£/mo)', field: 'parking' },
    { label: 'Permits (£/yr)', field: 'permits' },
    { label: 'Maintenance (£/yr)', field: 'scheduledMaintenance' },
    { label: 'Compliance (£/yr)', field: 'vehicleCompliance' },
    { label: 'Overhead (%)', field: 'overhead' },
  ];

  pushWide(
    'Standing Costs',
    'Quantity',
    vehicleTypes.map((v) => quantityByName[v.name] ?? 0),
  );
  for (const row of standingFields) {
    pushWide(
      'Standing Costs',
      row.label,
      vehicleTypes.map((v) => numOr(v[row.field], FLEET_STANDING_OP_DEFAULTS[row.field])),
    );
  }
  pushWide(
    'Standing Costs',
    'Total (£/yr/veh)',
    vehicleTypes.map((v) => fleetStandingAnnualGbp(v)),
  );

  // ── Trailer pool ─────────────────────────────────────────────────────────
  if (includeTrailerPool) {
    lines.push('');
    lines.push(csvRow(['Section', 'Field', 'Value']));
    lines.push(
      csvRow([
        'Trailer standing',
        'Standing (£/yr per trailer)',
        trailerStanding.standing_gbp_per_trailer_year,
      ]),
    );
    lines.push(csvRow(['Trailer standing', 'Trailer quantity', trailerStanding.trailer_quantity]));
    lines.push(
      csvRow([
        'Trailer standing',
        'Allocate to vehicle quantity',
        trailerStanding.allocate_to_vehicle_quantity,
      ]),
    );
    lines.push(
      csvRow([
        'Trailer standing',
        'Quantity source',
        trailerStanding.quantity_source ?? 'assets',
      ]),
    );
    lines.push(csvRow(['Trailer standing', 'Allocation', trailerStanding.allocation]));
    lines.push(
      csvRow([
        'Trailer standing',
        'Allocate to class',
        displayClass(trailerStanding.allocate_to_tonnage_class),
      ]),
    );
    for (const t of trailerStanding.trailer_types ?? []) {
      const label = `Type: ${t.name}`;
      lines.push(csvRow(['Trailer standing', `${label} quantity`, t.quantity]));
      lines.push(csvRow(['Trailer standing', `${label} acquisition`, ACQUISITION_METHOD_LABELS[acquisitionMethodOf(t)]]));
      lines.push(csvRow(['Trailer standing', `${label} capital (£/yr per trailer)`, capitalAnnualisedGbp(t)]));
      lines.push(csvRow(['Trailer standing', `${label} standing (£/yr per trailer)`, t.standing_gbp_per_trailer_year]));
      lines.push(csvRow(['Trailer standing', `${label} total (£/yr per trailer)`, trailerTypeAnnualPerTrailerGbp(t)]));
    }
  }

  // ── Running ──────────────────────────────────────────────────────────────
  const runHeaders = runningClassKeys.map(displayClass);
  lines.push('');
  lines.push(csvRow(['Section', 'Field', ...runHeaders]));

  const fuel = (profit?.fuel_cost_tonnage ?? {}) as Record<string, number | undefined>;
  const tyre = (profit?.tyre_cost_tonnage ?? {}) as Record<string, number | undefined>;
  const repair = (profit?.variable_repair_cost_tonnage ?? {}) as Record<string, number | undefined>;
  const driver = (profit?.driver_cost_tonnage ?? {}) as Record<
    string,
    ProfitabilityDriverCostRate | undefined
  >;

  const pushRunning = (field: string, get: (key: string) => number) => {
    lines.push(csvRow(['Running Costs', field, ...runningClassKeys.map(get)]));
  };

  pushRunning('Quantity', (key) => quantityByName[key] ?? 0);
  pushRunning('Fuel (£/mi)', (key) => fuel[key] ?? 0);
  pushRunning('Tyres (£/mi)', (key) => tyre[key] ?? 0);
  pushRunning('Repairs (£/mi)', (key) => repair[key] ?? 0);
  pushRunning('Driver (£/mi)', (key) => driver[key]?.mileage_rate ?? 0);
  pushRunning('Driving (£/hr)', (key) => driver[key]?.driving_hourly_rate ?? 0);
  pushRunning('Waiting (£/hr)', (key) => driver[key]?.waiting_hourly_rate ?? 0);

  return `${lines.join('\n')}\n`;
}

export function fleetCostAssumptionsExportFilename(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `vehicle-cost-rates-${y}-${m}-${d}.csv`;
}

export function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  // BOM so Excel (Windows) treats the file as UTF-8 — otherwise £ becomes "??".
  const body = mime.startsWith('text/csv') ? `\uFEFF${content}` : content;
  const blob = new Blob([body], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Convenience: build + download from live panel state. */
export function exportFleetCostAssumptionsCsv(
  input: FleetCostAssumptionsCsvInput,
  filename = fleetCostAssumptionsExportFilename(),
): void {
  downloadTextFile(filename, buildFleetCostAssumptionsCsv(input));
}
